'use client';

import {
  type ChangeEvent,
  type FC,
  type FormEvent,
  useContext,
  useState,
} from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Button, ButtonGroup, Flex, Password, Separator } from '@/_components';
import { setSignInMethod } from '@/_api/users';
import { LoginContext } from '@/_contexts/LoginProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { checkPasswordForError } from '@/_utils/passwords';
import { isSameOriginUrl } from '@/_utils/validations';
import type { SetPasswordProps } from './SetPassword.types';
import { useSignInWithCode } from '@/_hooks/useSignInWithCode';

export const SetPassword: FC<SetPasswordProps> = ({
  isStandalone,
  onBackOverride,
}) => {
  const { previousLoginState, setLoginState } = useContext(LoginContext);
  const { addToast } = useContext(ToastContext);
  const { setUser, user, token } = useContext(UserContext);
  const { push } = useRouter();
  const searchParams = useSearchParams();
  const { shouldShow2FA, send2FAandSetState } = useSignInWithCode();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [newPassword, setNewPassword] = useState<string>('');
  const [repeatedPassword, setRepeatedPassword] = useState<string>('');

  const onBack = () => {
    if (onBackOverride) return onBackOverride();

    setLoginState(previousLoginState || 'login');
  };

  const { isValid } = checkPasswordForError(newPassword, repeatedPassword);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!isValid) return setIsLoading(false);

    setIsLoading(true);

    try {
      const redirect: string | null = searchParams.get('redirect');
      const { error } = await setSignInMethod({
        signInMethod: 'password',
        newPassword,
        token,
      });

      if (!error) {
        setUser({
          ...user!,
          set_password_required: false,
          preferred_sign_in_method: 'password',
        });
      }

      if (shouldShow2FA) {
        return send2FAandSetState();
      }

      if (!error && isStandalone) {
        return push('/');
      }

      if (!error) {
        const redirectUrl =
          redirect && isSameOriginUrl(redirect) ? redirect : '/';

        return push(redirectUrl);
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  };

  const onNewPasswordChange = (event: ChangeEvent<HTMLInputElement>) =>
    setNewPassword(event.target.value);

  const onRepeatedPasswordChange = (event: ChangeEvent<HTMLInputElement>) =>
    setRepeatedPassword(event.target.value);

  return (
    <form onSubmit={onSubmit}>
      <Flex isColumn={true} gap={8} padding={10}>
        <Password
          label="New Password"
          value={newPassword}
          onChange={onNewPasswordChange}
          placeholder="Enter your password"
        />
        <Password
          label="Repeat Password"
          value={repeatedPassword}
          onChange={onRepeatedPasswordChange}
          showIndicator={true}
          confirmPassword={newPassword}
          placeholder="Enter your password"
        />
        <Separator isLight={true} />
        <ButtonGroup align="space-between" className="flex-row">
          {isStandalone ? (
            <div />
          ) : (
            <Button
              iconLeft="arrow-left"
              intent="ghost"
              onClick={onBack}
              type="button"
              value="Back"
            />
          )}
          <Button
            isDisabled={isLoading || !isValid}
            isLoading={isLoading}
            intent="primary"
            type="submit"
            value="Submit"
          />
        </ButtonGroup>
      </Flex>
    </form>
  );
};
